import React, { type ReactNode, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

type SmoothScrollProps = {
  children: ReactNode;
};

export default function SmoothScroll({ children }: SmoothScrollProps) {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    let lenis: Lenis | undefined;
    let unsubscribe: (() => void) | undefined;
    let refresh: number | undefined;
    let isDisposed = false;

    const onTick = (time: number) => lenis?.raf(time * 1000);
    const scrollToTop = () => {
      lenis?.scrollTo(0, { immediate: true });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };
    const stopSmoothScroll = () => lenis?.stop();
    const startSmoothScroll = () => lenis?.start();

    void import("lenis").then(({ default: Lenis }) => {
      if (isDisposed) {
        return;
      }

      lenis = new Lenis({
        anchors: true,
        lerp: 0.1,
        smoothWheel: true,
        syncTouch: false,
      });
      unsubscribe = lenis.on("scroll", ScrollTrigger.update);
      window.addEventListener("ux-eye-candy:scroll-top", scrollToTop);
      window.addEventListener("ux-eye-candy:modal-open", stopSmoothScroll);
      window.addEventListener("ux-eye-candy:modal-close", startSmoothScroll);
      gsap.ticker.add(onTick);
      gsap.ticker.lagSmoothing(0);
      refresh = requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    return () => {
      isDisposed = true;
      if (refresh) {
        cancelAnimationFrame(refresh);
      }
      unsubscribe?.();
      window.removeEventListener("ux-eye-candy:scroll-top", scrollToTop);
      window.removeEventListener("ux-eye-candy:modal-open", stopSmoothScroll);
      window.removeEventListener("ux-eye-candy:modal-close", startSmoothScroll);
      gsap.ticker.remove(onTick);
      lenis?.destroy();
    };
  }, []);

  return <>{children}</>;
}
